import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useAtomValue } from 'jotai'

import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import { deleteMail, fetchMails, updateMailStatus } from '@/api/mails.api'
import type { Mail } from '@/types/api'
import { currentUserAtom } from '@/store/auth.atom'
import { usePermission } from '@/hooks/use-permission'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportMails, useTableExport } from '@/api/dowload-excel.api'

import { buildColumns } from './columns'
import { MailFilterPanel } from './MailFilterPanel'
import type { MailFilterValues } from './MailFilterPanel'
import { MailDetailDrawer } from './MailDrawer'
import { MailFormDrawer } from './MailFormDrawer'
import { MailServerConfigPage } from './MailServerConfigPage'

const DEFAULT_FILTER: MailFilterValues = {
    mail: '',
    name: '',
    port: '',
    protocol: undefined,
    unitId: '',
    status: undefined,
    time: undefined,
}

export function MailsPage() {
    const currentUser = useAtomValue(currentUserAtom)

    if (currentUser?.userType !== 1) {
        return <MailServerConfigPage />
    }

    return <SphinxMailServer />
}

function SphinxMailServer() {
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    })

    const showUnitColumn = true

    const canCreate = usePermission('mail-server-create')
    const canView = usePermission('mail-server-view')
    const canUpdate = usePermission('mail-server-update')
    const canDelete = usePermission('mail-server-delete')
    const canExport = usePermission('mail-server-export')

    const [showFilter, setShowFilter] = useState(false)
    const [filter, setFilter] = useState<MailFilterValues>(DEFAULT_FILTER)
    const [activeFilters, setActiveFilters] = useState<MailFilterValues>(DEFAULT_FILTER)
    const [isExporting, setIsExporting] = useState(false)
    const [viewingMail, setViewingMail] = useState<Mail | null>(null)
    const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
    const [formMode, setFormMode] = useState<'create' | 'update'>('create')
    const [editingMailId, setEditingMailId] = useState<string | null>(null)
    const [deletingMail, setDeletingMail] = useState<Mail | null>(null)
    const [togglingMail, setTogglingMail] = useState<Mail | null>(null)

    const queryClient = useQueryClient()

    const filterParams = useMemo(() => {
        const fromDate = activeFilters.time?.from ? formatStartDate(activeFilters.time.from) : undefined
        const toDateRaw = activeFilters.time?.to || activeFilters.time?.from
        const toDate = toDateRaw ? formatEndDate(toDateRaw) : undefined

        return {
            page: pageIndex,
            limit: pageSize,
            mail: activeFilters.mail || undefined,
            name: activeFilters.name || undefined,
            port: activeFilters.port || undefined,
            protocol: activeFilters.protocol || undefined,
            unitId: activeFilters.unitId || undefined,
            status: activeFilters.status,
            fromDate,
            toDate,
        }
    }, [pageIndex, pageSize, activeFilters])

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['mails', filterParams],
        queryFn: ({ signal }) =>
            fetchMails(
                filterParams.page,
                filterParams.limit,
                {
                    mail: filterParams.mail,
                    name: filterParams.name,
                    port: filterParams.port,
                    protocol: filterParams.protocol,
                    unitId: filterParams.unitId,
                    status: filterParams.status,
                    fromDate: filterParams.fromDate,
                    toDate: filterParams.toDate,
                },
                signal,
            ),
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })
    const hasData = Boolean(data?.items && data.items.length > 0)

    const { mutate: toggleMailStatus, isPending: isToggling } = useMutation({
        mutationFn: (mail: Mail) => updateMailStatus(mail, mail.status === 1 ? 0 : 1),
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Cập nhật trạng thái mail thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mails'] })
        },
        onSettled: () => setTogglingMail(null),
    })

    const { mutate: removeMail, isPending: isRemoving } = useMutation({
        mutationFn: (mail: Mail) => deleteMail(mail.id),
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Xóa mail thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mails'] })
        },
        onSettled: () => setDeletingMail(null),
    })

    const columns = useMemo(
        () =>
            buildColumns({
                pageIndex,
                pageSize,
                showUnitColumn,
                canView,
                canUpdate,
                canDelete,
                onViewDetail: (mail) => setViewingMail(mail),
                onUpdate: (mail) => {
                    setFormMode('update')
                    setEditingMailId(mail.id)
                    setIsFormDrawerOpen(true)
                },
                onDelete: (mail) => setDeletingMail(mail),
                onToggleStatus: (mail) => setTogglingMail(mail),
            }),
        [pageIndex, pageSize, showUnitColumn, canView, canUpdate, canDelete],
    )

    const rows = useMemo(() => data?.items ?? [], [data?.items])

    const { handleExport } = useTableExport<Mail>(
        'Danh sách mail server',
        columns,
        async (payload) => {
            try {
                setIsExporting(true)
                await exportMails(filterParams, payload, 'danh-sach-mail-server.xlsx')
            } finally {
                setIsExporting(false)
            }
        },
    )

    if (isLoading) {
        return (
            <section className="space-y-4">
                <PageHeader title="DANH SÁCH MAIL" isLoading />
                <div className="mt-6 text-center">Đang tải...</div>
            </section>
        )
    }

    return (
        <section className="space-y-4">
            <PageHeader
                title="DANH SÁCH MAIL"
                onToggleFilter={() => setShowFilter((v) => !v)}
                onExport={canExport ? handleExport : undefined}
                disabled={!hasData}
                isExporting={isExporting}
            >
                {canCreate && (
                    <Button
                        variant="primary"
                        size="md"
                        className="flex-1 gap-2 h-10"
                        onClick={() => {
                            setFormMode('create')
                            setEditingMailId(null)
                            setIsFormDrawerOpen(true)
                        }}
                    >
                        <Plus size={16} />
                        Thêm mới
                    </Button>
                )}
            </PageHeader>

            {showFilter && (
                <MailFilterPanel
                    values={filter}
                    onChange={setFilter}
                    onSearch={() => {
                        const trimmedFilter = {
                            ...filter,
                            mail: filter.mail?.trim() || '',
                            name: filter.name?.trim() || '',
                        }
                        setFilter(trimmedFilter)
                        setActiveFilters(trimmedFilter)
                        setPagination((s) => ({ ...s, pageIndex: 0 }))
                        refetch()
                    }}
                    showUnitColumn={showUnitColumn}
                />
            )}

            <DataTable
                columns={columns}
                data={rows}
                tableOptions={{
                    state: { pagination: { pageIndex, pageSize } },
                    onPaginationChange: setPagination,
                    getPaginationRowModel: getPaginationRowModel(),
                    getCoreRowModel: getCoreRowModel(),
                    manualPagination: true,
                    pageCount: data?.totalPages,
                }}
                footer={
                    <Pagination
                        page={pageIndex + 1}
                        pageCount={data?.totalPages ?? 1}
                        pageSize={pageSize}
                        totalCount={data?.total ?? 0}
                        onPageChange={(p) => setPagination((s) => ({ ...s, pageIndex: p - 1 }))}
                        onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
                    />
                }
            />

            {viewingMail && (
                <MailDetailDrawer
                    mailId={viewingMail.id}
                    showUnitColumn={showUnitColumn}
                    onClose={() => setViewingMail(null)}
                    onUpdate={(mail) => {
                        setViewingMail(null)
                        setFormMode('update')
                        setEditingMailId(mail.id)
                        setIsFormDrawerOpen(true)
                    }}
                />
            )}

            {isFormDrawerOpen && (
                <MailFormDrawer
                    isOpen={isFormDrawerOpen}
                    mode={formMode}
                    mailId={editingMailId}
                    onClose={() => {
                        setIsFormDrawerOpen(false)
                        setEditingMailId(null)
                    }}
                />
            )}

            <ConfirmModal
                isOpen={!!togglingMail}
                title="Xác nhận"
                description={<>Bạn chắc chắn muốn {togglingMail?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} mail <strong>{togglingMail?.mail}</strong> này?</>}
                variant="info"
                isLoading={isToggling}
                onClose={() => setTogglingMail(null)}
                onConfirm={() => togglingMail && toggleMailStatus(togglingMail)}
            />

            <ConfirmModal
                isOpen={!!deletingMail}
                title="Xóa mail"
                description={<>Bạn có chắc chắn muốn xóa mail <strong>{deletingMail?.mail}</strong> này?</>}
                variant="danger"
                isLoading={isRemoving}
                onClose={() => setDeletingMail(null)}
                onConfirm={() => deletingMail && removeMail(deletingMail)}
            />
        </section>
    )
}